import { SortDirection } from '@mui/material/TableCell';
import { useMemo } from 'react';
import { VendorItemsListOptions } from '.';
import { useUsers } from '../../contexts/userContext';
import useCurrentSite from '../../utils/hooks/useCurrentSite';
import { getStoredLang } from '../../utils/langUtils/langUtils';
import { InvoiceStatus } from '../models/Invoice';
import { FilterValueRange } from '../types';
import { MobileInventoryStatus } from '../models/Location';

const useQueryKeys = () => {
  const { siteId: enterpriseUnitId, currentSiteId } = useCurrentSite();
  const { firebaseToken, org } = useUsers();

  // Query keys dependant on EUID updates:
  const queryKeys = useMemo(() => {
    const rawItemsKeys = {
      all: ['raw items', org?.bslId] as const,
      details: () => [...rawItemsKeys.all, 'detail'] as const,
      detailsById: (rawItemId: string) => [...rawItemsKeys.details(), rawItemId] as const,
      customUnitValidation: (rawItemId: string) =>
        [...rawItemsKeys.detailsById(rawItemId), 'customUnitValidation'] as const,
      costCalcMethodOptions: ['raw items', 'costCalcMethodOptions'] as const,
      getRawItemUnitClasses: (rawItemId: string) =>
        [...rawItemsKeys.detailsById(rawItemId), 'getRawItemUnitClasses'] as const,
      recipeUnitById: (rawItemId: string) => [...rawItemsKeys.details(), rawItemId, 'recipeUnit'] as const,
      usagePer: (rawItemId: string) => [...rawItemsKeys.detailsById(rawItemId), 'usagePer', enterpriseUnitId] as const,
      defaultUsagePerFactor: [org?.bslId, 'defaultUsagePerFactor'] as const,
      reportingUnitById: (rawItemId: string) => [...rawItemsKeys.details(), rawItemId, 'reportingUnit'] as const,
      list: () => [...rawItemsKeys.all, 'list'] as const,
      listAllByLocation: (locationId: string, includePrepItems?: boolean) =>
        [...rawItemsKeys.list(), 'all', locationId, includePrepItems] as const,
      listWithPage: (page: number, limit: number, find?: string, filters?: string[], active?: boolean, sort?: string) =>
        [
          ...rawItemsKeys.list(),
          { paginated: page, limit, filters, active, sort },
          ...[find ? { filtered: find } : {}],
        ] as const,
      listByLocation: (
        locationId: string,
        page: number,
        limit: number,
        getSelectedOnly?: boolean,
        filters?: string[],
        sort?: string,
        sortOrder?: SortDirection,
        includePrepItems?: boolean
      ) =>
        [
          ...rawItemsKeys.list(),
          locationId,
          { paginated: page, limit, filters, getSelectedOnly, sort, sortOrder, includePrepItems },
        ] as const,
      listWithUnitsPage: (
        rawItemId: string | undefined,
        page: number,
        limit: number,
        find?: string,
        filter?: string,
        sort?: string
      ) =>
        [
          ...rawItemsKeys.list(),
          rawItemId,
          { paginated: page, limit, filter, sort },
          ...[find ? { filtered: find } : {}],
        ] as const,
      calculateUsagePer: (siteId: string, startDate: string, endDate: string) =>
        [...rawItemsKeys.all, 'calculate-usage', siteId, startDate, endDate] as const,
      getLastCalculated: (siteId: string) => [...rawItemsKeys.all, 'get-last-calculated', siteId] as const,
    };

    const invoicesKeys = {
      all: ['invoices', enterpriseUnitId, firebaseToken] as const,
      list: () => [...invoicesKeys.all, 'list'] as const,
      listWithPage: (page: number, limit: number, filters?: string[], sort?: string) =>
        [...invoicesKeys.list(), { paginated: page, limit, filters, sort }] as const,
      listByVendorId: (vendorId: string, status?: InvoiceStatus) =>
        [...invoicesKeys.list(), vendorId, status ? status.toString() : ''] as const,
      details: () => [...invoicesKeys.all, 'detail'] as const,
      detailsById: (invoiceId: string) => [...invoicesKeys.details(), invoiceId] as const,
    };

    const locationKeys = {
      all: ['locations', currentSiteId, firebaseToken] as const,
      list: (includePrepItems?: boolean) => [...locationKeys.all, 'list', includePrepItems] as const,
    };

    const inventoryListKeys = {
      all: ['inventory lists', enterpriseUnitId, firebaseToken] as const,
      listWithPage: (
        page?: number,
        limit?: number,
        filters?: string[],
        sortBy?: string,
        sortOrder?: SortDirection,
        includePrepItems?: boolean
      ) =>
        [...inventoryListKeys.all, { paginated: page, limit, filters, sortBy, sortOrder, includePrepItems }] as const,
      listById: (
        listId: string,
        showAssigned: boolean,
        page: number,
        limit: number,
        filter?: string,
        sort?: string,
        includePrepItems?: boolean
      ) =>
        [
          ...inventoryListKeys.all,
          listId,
          showAssigned,
          { paginated: page, limit, filter, sort, includePrepItems },
        ] as const,
    };

    const inventorySummaryKeys = {
      all: ['inventory summary', enterpriseUnitId, firebaseToken] as const,
      list: (
        statuses?: MobileInventoryStatus[],
        minValue?: number,
        maxValue?: number,
        startDate?: string | null,
        endDate?: string | null,
        minSkippedPercentage?: number,
        maxSkippedPercentage?: number,
        includePrepItems?: boolean
      ) =>
        [
          'inventory summary',
          enterpriseUnitId,
          firebaseToken,
          'list',
          {
            statuses,
            minValue,
            maxValue,
            startDate,
            endDate,
            minSkippedPercentage,
            maxSkippedPercentage,
            includePrepItems,
          },
        ] as const,
      listById: (id: string, includePrepItems?: boolean) =>
        ['inventory summary', enterpriseUnitId, firebaseToken, 'list', id, includePrepItems] as const,
      byLocation: (summaryId: string, locationId: string, searchValue?: string, includePrepItems?: boolean) =>
        [
          'inventory summary',
          enterpriseUnitId,
          firebaseToken,
          'byLocation',
          summaryId,
          locationId,
          searchValue ?? '',
          includePrepItems,
        ] as const,
    };

    const invoiceItemsKeys = {
      all: ['invoice items', enterpriseUnitId, firebaseToken] as const,
      list: () => [...invoiceItemsKeys.all, 'list'] as const,
      listById: (invoiceId: string) => [...invoiceItemsKeys.list(), invoiceId] as const,
    };

    const vendorsKeys = {
      all: ['vendors', enterpriseUnitId, firebaseToken] as const,
      detail: () => [...vendorsKeys.all, 'detail'] as const,
      detailsById: (vendorId: string) => [[...vendorsKeys.detail(), vendorId]] as const,
      list: () => [...vendorsKeys.all, 'list'] as const,
      listWithQuery: (query?: string) => [...vendorsKeys.list(), ...[query ? { filtered: query } : {}]] as const,
      listWithNameQuery: (query?: string, active?: boolean | undefined, limit?: number) =>
        [...vendorsKeys.list(), { active, limit }, ...[query ? { filtered: query } : {}]] as const,
      listWithPage: (page: number, limit: number, query?: string[], sort?: string, active?: boolean) =>
        [...vendorsKeys.list(), { paginated: page, limit, query, sort, active }] as const,
    };

    const purchaseOrderKeys = {
      all: ['purchase-orders', enterpriseUnitId, firebaseToken] as const,
      gridData: (page: number, limit: number, filters?: string[], sort?: string) =>
        [...purchaseOrderKeys.all, 'grid-data', page, limit, filters, sort] as const,
      byId: (purchaseOrderId: string) => [...purchaseOrderKeys.all, purchaseOrderId],
      acknowledgementStatusById: (purchaseOrderId: string) =>
        [...purchaseOrderKeys.all, purchaseOrderId, 'acknowledgement-status'] as const,
      itemsById: (purchaseOrderId: string) => [...purchaseOrderKeys.byId(purchaseOrderId), 'items'] as const,
      vendorItemDetailsById: (vendorId: string, vendorItemId: string, deliveryDate: string) =>
        [...purchaseOrderKeys.all, 'vendor-item-details', vendorId, vendorItemId, deliveryDate] as const,
      purchaseOrderItemById: (purchaseOrderId: string, purchaseOrderItemId: string) =>
        [...purchaseOrderKeys.itemsById(purchaseOrderId), purchaseOrderItemId] as const,
    };

    const vendorItemsKeys = {
      all: ['vendor items', enterpriseUnitId, firebaseToken] as const,
      list: () => [...vendorItemsKeys.all, 'list'] as const,
      listById: (vendorId: string, options?: VendorItemsListOptions) =>
        [...vendorItemsKeys.list(), vendorId, options ?? {}] as const,
      listUnmappedItemsById: (vendorId: string, options?: VendorItemsListOptions) =>
        [...vendorItemsKeys.list(), vendorId, options ?? {}] as const,
      getMappedVendorItems: (rawItemId: string | undefined) =>
        [...vendorItemsKeys.list(), rawItemId, 'getAllUnmapped'] as const,
      listWithRawItemAndPage: (
        rawItemId: string | undefined,
        page: number,
        limit: number,
        addReceivingUnits?: boolean,
        addVendor?: boolean,
        addRawItems?: boolean,
        onlyUnmapped?: boolean,
        filters?: string[],
        sort?: string
      ) =>
        [
          ...vendorItemsKeys.list(),
          rawItemId,
          { paginated: page, limit },
          ...[addReceivingUnits ? { addReceivingUnits } : {}],
          ...[addVendor ? { addVendor } : {}],
          ...[addRawItems ? { addRawItems } : {}],
          ...[onlyUnmapped ? { onlyUnmapped } : {}],
          filters,
          sort,
        ] as const,
    };

    const vendorTemplateKeys = {
      all: ['vendor-templates', enterpriseUnitId, firebaseToken] as const,
      list: (vendorIds: string[], templateName: string, page: number, limit: number) =>
        [...vendorTemplateKeys.all, 'list', { vendorIds, templateName, page, limit }] as const,
      filters: () => [...vendorTemplateKeys.all, 'filter-options'] as const,
    };

    return {
      invoices: invoicesKeys,
      invoiceItems: invoiceItemsKeys,
      vendors: vendorsKeys,
      vendorItems: vendorItemsKeys,
      rawItems: rawItemsKeys,
      locations: locationKeys,
      inventoryLists: inventoryListKeys,
      purchaseOrders: purchaseOrderKeys,
      inventorySummary: inventorySummaryKeys,
      vendorTemplates: vendorTemplateKeys,
    };
  }, [enterpriseUnitId, currentSiteId, firebaseToken, org?.bslId]);

  // Non EUID-dependent queries:

  const serviceItemsKeys = {
    all: ['service items', firebaseToken] as const,
    list: () => [...serviceItemsKeys.all, 'list'] as const,
    listWithFilter: (filter: string) => [...serviceItemsKeys.all, 'list', filter] as const,
  };

  const unitsKeys = {
    all: ['units', enterpriseUnitId, firebaseToken, getStoredLang()] as const,
    container: () => [...unitsKeys.all, 'container'] as const,
    containerWithStarting: () => [...unitsKeys.all, 'container', 'withStarting'] as const,
    industry: () => [...unitsKeys.all, 'industry'] as const,
    industryWithReceiving: () => [...unitsKeys.all, 'industry', 'withReceiving'] as const,
    receiving: {
      all: () => [...unitsKeys.all, 'receiving'] as const,
      details: () => [...unitsKeys.receiving.all(), 'detail'] as const,
      detailsById: (invoiceId: string) => [...unitsKeys.receiving.details(), invoiceId] as const,
    },
    custom: {
      all: () => [...unitsKeys.all, 'custom'] as const,
      isInUse: (customUnitId: string | undefined) => [...unitsKeys.all, 'custom', customUnitId] as const,
    },
    combined: {
      listWithPage: (page: number, limit: number, filters: string[], sortBy: string, sortOrder: string) =>
        [...unitsKeys.all, 'combined', { paginated: page, limit, filters }, sortBy, sortOrder] as const,
    },
  };

  const categoryKeys = {
    all: ['categories', firebaseToken] as const,
    byId: (categoryId: string) => [...categoryKeys.all, categoryId],
  };

  const invHistVendorSummaryKeys = {
    all: ['invoice-history-vendors'] as const,
    kpiCards: {
      invoiceCount: (
        startDate: string,
        endDate: string,
        siteIds: string[],
        previousStartDate: string,
        previousEndDate: string,
        vendors?: string[]
      ) =>
        [
          ...invHistVendorSummaryKeys.all,
          'kpi-cards',
          'invoice-count',
          startDate,
          endDate,
          siteIds,
          previousStartDate,
          previousEndDate,
          vendors,
        ] as const,
      invoiceAmount: (
        startDate: string,
        endDate: string,
        siteIds: string[],
        previousStartDate: string,
        previousEndDate: string,
        vendors?: string[]
      ) =>
        [
          ...invHistVendorSummaryKeys.all,
          'kpi-cards',
          'invoice-amount',
          startDate,
          endDate,
          siteIds,
          previousStartDate,
          previousEndDate,
          vendors,
        ] as const,
      totalByVendor: (startDate: string, endDate: string, siteIds: string[], vendors?: string[]) =>
        [
          ...invHistVendorSummaryKeys.all,
          'kpi-cards',
          'total-by-vendor',
          startDate,
          endDate,
          siteIds,
          vendors,
        ] as const,
    },
    gridData: (startDate: string, endDate: string, siteIds: string[], vendors?: string[]) =>
      [...invHistVendorSummaryKeys.all, 'grid-data', startDate, endDate, siteIds, vendors] as const,
  };

  const invHistVendorDetailsKeys = {
    all: ['invoice-history-vendors', 'vendor-details'] as const,
    kpiCards: (startDate: string, endDate: string, siteIds: string[], vendor: string) =>
      [...invHistVendorDetailsKeys.all, 'kpi-cards', startDate, endDate, siteIds, vendor] as const,
    gridData: (startDate: string, endDate: string, siteIds: string[], vendor: string) =>
      [...invHistVendorDetailsKeys.all, 'grid-data', startDate, endDate, siteIds, vendor] as const,
  };

  const invHistGLAccountDetailsKeys = {
    all: ['invoice-history', 'by-category', 'details'] as const,
    kpiCards: (
      startDate: string,
      endDate: string,
      siteIds: string[],
      glSubcategories: string[],
      glAccounts: string[],
      vendorIds?: string[]
    ) =>
      [
        ...invHistGLAccountDetailsKeys.all,
        'kpi-numbers',
        startDate,
        endDate,
        siteIds,
        glSubcategories,
        glAccounts,
        vendorIds,
      ] as const,
    gridData: (
      startDate: string,
      endDate: string,
      siteIds: string[],
      glSubcategories: string[],
      glAccounts: string[],
      vendorIds?: string[]
    ) =>
      [
        ...invHistGLAccountDetailsKeys.all,
        'grid-data',
        startDate,
        endDate,
        siteIds,
        glSubcategories,
        glAccounts,
        vendorIds,
      ] as const,
  };

  const recipeKeys = {
    detailsById: (recipeId: string) => ['recipes-detail', recipeId, org?.bslId],
    list: () => ['recipes-list', org?.bslId],
    listWithPaginatedRecipes: ['recipes-paginated', org?.bslId],
    listWithPage: (
      page: number,
      limit: number,
      query?: string[],
      gridFilters?: string[],
      sort?: string,
      active?: boolean
    ) =>
      [...recipeKeys.listWithPaginatedRecipes, { paginated: page, limit, query, gridFilters, sort, active }] as const,
    unassignedItems: (recipeId?: string, filterPrepItem?: boolean, find?: string) =>
      ['recipes-unassigned-items', recipeId, filterPrepItem, find, org?.bslId] as const,
    filterOptions: (page?: number, limit?: number) =>
      ['recipes-filter-options', { paginated: page ?? 1, limit: limit ?? 100 }, org?.bslId] as const,
    filterOptionsByField: (page: number, limit: number, field: string, value: string | undefined) =>
      ['recipes-filter-options-by-field', { paginated: page, limit, field, value }, org?.bslId] as const,
  };

  const recipeItemKeys = {
    listWithPaginatedIngredients: ['recipe-items-paginated'],
    listWithPage: (id: string, page: number, limit: number, query?: string[], sort?: string, active?: boolean) =>
      [...recipeItemKeys.listWithPaginatedIngredients, id, { paginated: page, limit, query, sort, active }] as const,
  };

  const salesItemsKeys = {
    all: ['sales-items', org?.bslId] as const,
    gridData: () => [...salesItemsKeys.all, 'grid'] as const,
    byItemCode: (itemCode: string) => [...salesItemsKeys.all, itemCode] as const,
    mappings: (filterPrepItem?: boolean, find?: string) =>
      [...salesItemsKeys.all, 'mappings', filterPrepItem, find] as const,
    unmappedSuggestions: (keyword?: string, limit?: number, page?: number) =>
      [...salesItemsKeys.all, 'unmapped-suggestions', { keyword, limit, page }] as const,
  };

  const modifierGroupsKeys = {
    gridData: () => ['modifier-groups', 'grid'],
    byItemCode: (itemCode: string) => ['modifier-groups', itemCode],
  };

  const cogsKeys = {
    all: ['cogs'],
    dataGrid: (startDate: string, endDate: string, siteIds: string[]) => [
      ...cogsKeys.all,
      'data-grid',
      startDate,
      endDate,
      siteIds,
    ],
    filters: (startDate: string, endDate: string, siteIds: string[]) => [
      ...cogsKeys.all,
      'filters',
      startDate,
      endDate,
      siteIds,
    ],
    availableDates: (siteId: string) => [...cogsKeys.all, 'available-dates', siteId],
    kpiCards: (
      startDate: string,
      endDate: string,
      siteIds: string[],
      glAccounts?: string[],
      reportingUnits?: string[],
      itemPrice?: FilterValueRange,
      daysOnHand?: FilterValueRange
    ) => [
      ...cogsKeys.all,
      'kpi-cards',
      startDate,
      endDate,
      siteIds,
      glAccounts,
      reportingUnits,
      itemPrice?.min,
      itemPrice?.max,
      daysOnHand?.min,
      daysOnHand?.max,
    ],
  };

  const top20ItemUsageKeys = {
    all: ['top20-item-usage'],
    gridData: (startDate: string, endDate: string, siteId: string, varianceType: string) => [
      ...top20ItemUsageKeys.all,
      'report-data',
      startDate,
      endDate,
      siteId,
      varianceType,
    ],
  };
  const perpetualInventoryKeys = {
    all: ['perpetual-inventory'],
    dataGrid: (businessDate: string, siteId: string) => [
      ...perpetualInventoryKeys.all,
      'data-grid',
      businessDate,
      siteId,
    ],
    kpiCards: (businessDate: string, siteId: string) => [
      ...perpetualInventoryKeys.all,
      'kpi-cards',
      businessDate,
      siteId,
    ],
  };

  const lowStockReportKeys = {
    all: ['low-stock-report'],
    data: (siteId: string, date?: string) => [...lowStockReportKeys.all, 'data', siteId, date ?? 'current'],
  };

  const realTimeInventoryKeys = {
    all: ['real-time-inventory'] as const,
    rawItemsOnHand: (
      page: number,
      limit: number,
      sortBy: string,
      sortDir: string,
      filterStatus: string,
      siteId: string,
      itemName?: string
    ) => [
      ...realTimeInventoryKeys.all,
      'raw-items-on-hand',
      page,
      limit,
      sortBy,
      sortDir,
      filterStatus,
      siteId,
      itemName,
    ],
  };

  return {
    ...queryKeys,
    recipes: recipeKeys,
    recipeItems: recipeItemKeys,
    categories: categoryKeys,
    units: unitsKeys,
    serviceItems: serviceItemsKeys,
    invHistVendorSummary: invHistVendorSummaryKeys,
    invHistVendorDetails: invHistVendorDetailsKeys,
    invHistGLAccountDetails: invHistGLAccountDetailsKeys,
    salesItems: salesItemsKeys,
    modifierGroups: modifierGroupsKeys,
    cogs: cogsKeys,
    top20ItemUsage: top20ItemUsageKeys,
    perpetualInventory: perpetualInventoryKeys,
    lowStockReport: lowStockReportKeys,
    realTimeInventory: realTimeInventoryKeys,
  };
};

export default useQueryKeys;
